// DecisionIntelligenceSummaryTile.jsx — iter269
//
// Renders the Decision Intelligence Summary Report inline as four
// collapsible sections, pulling from the existing
// GET /api/admin/orchestrator/summary-report endpoint. No new endpoint.
//
// Sections (collapsible, all open by default):
//   1. What Happened           — every Orchestrator decision in plain language
//   2. Why It Did It           — reasoning bullets per decision
//   3. Actions Requiring Review — ranked recommendations from bdl_config
//   4. System Health           — one-line per-city roll-up

import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Section({ id, title, children, defaultOpen = true, count = null }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-slate-200" data-testid={`dis-section-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-slate-50"
        data-testid={`dis-section-toggle-${id}`}
      >
        <span className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">
            {title}
          </span>
          {count !== null && (
            <span className="text-[10px] text-slate-400">({count})</span>
          )}
        </span>
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

const STATUS_DOT = {
  clean: "bg-emerald-400",
  has_warnings: "bg-amber-400",
  needs_review: "bg-rose-500",
};

export default function DecisionIntelligenceSummaryTile() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchReport = async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await axios.get(`${API}/admin/orchestrator/summary-report?hours=24`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) {
          setReport(r.data);
          setError(null);
        }
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchReport();
    const id = setInterval(fetchReport, 60_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 p-4">
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading Decision Intelligence Summary...
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-xs text-rose-600 p-4" data-testid="dis-error">
        {error}
      </div>
    );
  }
  if (!report) return null;

  const t = report.totals || {};

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="decision-intelligence-summary-tile"
    >
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800">
            Decision Intelligence Summary
          </div>
          <div className="text-[10px] text-slate-500">
            {t.cities || 0} cities · {t.decisions || 0} decisions ·{" "}
            <span className={t.human_review_actions > 0 ? "text-rose-600 font-semibold" : ""}>
              {t.human_review_actions || 0} need review
            </span>
          </div>
        </div>
      </div>

      <Section id="what-happened" title="1. What Happened" count={t.decisions || 0}>
        {(report.what_happened || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic">No decisions in the last 24 hours.</div>
        ) : (
          <div className="space-y-3">
            {report.what_happened.map((c) => (
              <div key={c.city_id}>
                <div className="text-xs font-medium text-slate-700">{c.city_name}</div>
                <ul className="mt-1 space-y-1 ml-3">
                  {c.decisions.map((d, i) => (
                    <li
                      key={i}
                      className="text-[11px] text-slate-600 list-disc"
                      data-testid={`dis-what-happened-${c.city_id}-${i}`}
                    >
                      {d.situation_label}. <span className="font-medium">{d.decision_label}.</span>{" "}
                      Confidence: {d.confidence_pct}. Mode: {d.mode_label}.
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section id="why" title="2. Why It Did It">
        {(report.reasoning_by_city || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic">No reasoning recorded this run.</div>
        ) : (
          <div className="space-y-3">
            {report.reasoning_by_city.map((c) => (
              <div key={c.city_id}>
                <div className="text-xs font-medium text-slate-700">{c.city_name}</div>
                {c.items.map((it, i) => (
                  <div key={i} className="ml-3 mt-1">
                    <div className="text-[11px] text-slate-500 italic">{it.situation_label}</div>
                    <ul className="ml-3 space-y-0.5">
                      {it.bullets.map((b, j) => (
                        <li
                          key={j}
                          className="text-[11px] text-slate-600 list-disc"
                          data-testid={`dis-reasoning-${c.city_id}-${i}-${j}`}
                        >
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        id="human-review"
        title="3. Actions Requiring Human Review"
        count={t.human_review_actions || 0}
      >
        {(report.actions_requiring_human_review || []).length === 0 ? (
          <div className="text-xs text-emerald-700 italic">
            No actions required human review.
          </div>
        ) : (
          <div className="space-y-3">
            {report.actions_requiring_human_review.map((c) => (
              <div key={c.city_id}>
                <div className="text-xs font-medium text-rose-800">{c.city_name}</div>
                {c.actions.map((a, i) => (
                  <div key={i} className="ml-3 mt-1">
                    <div
                      className="text-[11px] text-slate-700"
                      data-testid={`dis-action-${c.city_id}-${i}`}
                    >
                      {a.situation_label} — {a.reason}
                    </div>
                    {a.ranked_recommendations && a.ranked_recommendations.length > 0 && (
                      <ol className="ml-3 mt-1 space-y-0.5 list-decimal text-[11px] text-slate-600">
                        {a.ranked_recommendations.map((r, j) => (
                          <li key={j} data-testid={`dis-rec-${c.city_id}-${i}-${j}`}>
                            <span className="font-medium">{r.label}</span>
                            {r.rationale && (
                              <span className="text-slate-500"> — {r.rationale}</span>
                            )}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        id="health"
        title="4. System Health"
        count={(report.system_health_summary || []).length}
      >
        {(report.system_health_summary || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic">No cities reported.</div>
        ) : (
          <div className="space-y-1">
            {report.system_health_summary.map((h) => (
              <div
                key={h.city_id}
                className="flex items-center gap-2 text-[11px]"
                data-testid={`dis-health-${h.city_id}`}
              >
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full ${
                    STATUS_DOT[h.status] || "bg-slate-300"
                  }`}
                />
                <span className="font-medium text-slate-700 w-32 truncate">
                  {h.city_name}
                </span>
                <span className="text-slate-500">
                  decisions={h.decision_count}, review={h.human_review_count}
                </span>
                {h.informational_flags && h.informational_flags.length > 0 && (
                  <span className="text-amber-700">
                    flags: {h.informational_flags.join(", ")}
                  </span>
                )}
                <span className="ml-auto text-slate-400">{h.status}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
